import { env } from '../config/env';
import { API_HEADERS } from '../constants/apiHeaders';
import api from '../utils/api';
import { APPLICANT_ENDPOINTS } from './endpoints/rec/applicant.endpoints';
import { RESUME_ENDPOINTS } from './endpoints/rec/resume.endpoints';
import { VACANCY_ENDPOINTS } from './endpoints/rec/vacancy.endpoints';
import { SYSTEM_ENDPOINTS } from './endpoints/system.endpoints';
import type { ApplicantResponse } from '../components/common/table/types';
import type { ApplicantMessage, ApplicantMessagesResponse } from './types/applicant.types';

export type FetchApplicantsParams = {
  status?: string | null;
  source?: string | null;
  vacancyCode?: string | null;
  vacancyOrName?: string | null;
  email?: string | null;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
  sort?: string;
};

export const applicantService = {
  search: (params: FetchApplicantsParams) =>
    api.get<ApplicantResponse>(APPLICANT_ENDPOINTS.search, {
      params,
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  getFeedback: (applicantId: number) =>
    api.get(APPLICANT_ENDPOINTS.feedback(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  generateCVComparisonFeedback: (applicantId: number) =>
    api.get(APPLICANT_ENDPOINTS.generateCVComparisonFeedback(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  generateCvEmail: (applicantId: number, email: string) =>
    api.get(`${RESUME_ENDPOINTS.generateApplicantCv}?id=${applicantId}&email=${email}`, {
      headers: { apikey: env.cvApiKey },
    }),

  getVacancyCodes: () =>
    api.get<{ data: { vacancy_Code: string; value: string }[] }>(VACANCY_ENDPOINTS.vacancyCodes, {
      headers: { ...API_HEADERS.VACANCY_CODES_LOOKUP },
    }),

  getCountries: () =>
    api.get<{ data: { id: number; name: string; currency: string }[] }>(SYSTEM_ENDPOINTS.countries, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_COUNTRIES },
    }),

  updateVacancyCode: (payload: { applicantId: number; issueId: string | null; vacancyCode: string }) =>
    api.put(APPLICANT_ENDPOINTS.updateVacancyCode, payload, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_UPDATE_VACANCY_CODE },
    }),

  updateCountry: (applicantId: number, payload: { countryId: number }) =>
    api.put(APPLICANT_ENDPOINTS.updateCountry(applicantId), payload, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_UPDATE_COUNTRY },
    }),

  addApplicant: (formData: FormData) =>
    api.post(APPLICANT_ENDPOINTS.add, formData, {
      headers: {
        ...API_HEADERS.ADD_APPLICANT_SUBMIT,
        'Content-Type': 'multipart/form-data',
      },
    }),

  getJiraComments: <T = { comments: unknown[] }>(applicantId: string) =>
    api.get<T>(APPLICANT_ENDPOINTS.jiraComments(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_JIRA_COMMENTS },
    }),

  getJiraAttachmentBlob: (issueId: string, filename: string) =>
    api.get<Blob>(APPLICANT_ENDPOINTS.jiraAttachment(issueId, filename), {
      headers: { ...API_HEADERS.APPLICANT_JIRA_COMMENTS },
      responseType: 'blob',
    }),

  getFullProfile: <T = unknown>(applicantId: string) =>
    api.get<T>(APPLICANT_ENDPOINTS.byId(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_PROFILE_FULL_GET },
    }),

  updateProfile: (applicantId: string, payload: unknown) =>
    api.put(APPLICANT_ENDPOINTS.updateProfile(applicantId), payload, {
      headers: { ...API_HEADERS.APPLICANT_PROFILE_UPDATE },
    }),

  getMessages: (applicantId: string) =>
    api.get<ApplicantMessagesResponse | ApplicantMessage[]>(APPLICANT_ENDPOINTS.messages(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_MESSAGES_LIST },
    }),

  sendWelcomeMessage: (applicantId: number) =>
    api.post(
      APPLICANT_ENDPOINTS.sendWelcome,
      { applicantId, communication_medium: 'WHATSAPP' },
      { headers: { ...API_HEADERS.APPLICANT_MESSAGES_LIST } },
    ),

  manualWelcome: (applicantId: number, triggeredBy: string) =>
    api.post<import('./types/applicant.types').ManualWelcomeResponse>(
      APPLICANT_ENDPOINTS.manualWelcome,
      { applicantId, triggeredBy },
      { headers: { ...API_HEADERS.SEND_WELCOME_MESSAGE } },
    ),

  /**
   * Manual "Allocate & Trigger" of one vacancy stage. HTTP 200 = started, HTTP 400 = not started
   * (same StageRunResult body), so 400 is resolved instead of thrown and callers read `started`.
   */
  triggerStage: (applicantId: number, vacancyStageId: number, triggeredBy: string) =>
    api.post<import('./types/applicant.types').StageRunResult>(
      APPLICANT_ENDPOINTS.triggerStage(applicantId, vacancyStageId),
      { triggeredBy },
      {
        headers: { ...API_HEADERS.MANUAL_INTERVIEW_INIT },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
      },
    ),

  /** Stages the applicant has reached, with progress, questions and the manual action. */
  getApplicantStages: (applicantId: number) =>
    api.get<import('./types/applicant.types').ApplicantStagesResponse>(
      APPLICANT_ENDPOINTS.applicantStages(applicantId),
      { headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_QUESTION_ANSWERS } },
    ),

  testSendMessage: (applicantId: number, message: string) =>
    api.post(APPLICANT_ENDPOINTS.testSend, { applicantId, message }, {
      headers: { ...API_HEADERS.SEND_MESSAGE_SEND },
    }),

  // Process Status - history popup only (grid status comes from /search)
  getProcessHistory: (applicantId: number) =>
    api.get<import('./types/processStatus.types').ProcessHistoryResponse>(
      APPLICANT_ENDPOINTS.processHistory(applicantId),
      {
        headers: { ...API_HEADERS.APPLICANT_SEARCH },
      }
    ),
};
